import cn from 'classnames'
import { ClipboardEvent, FC, forwardRef, useCallback, useMemo, useState } from 'react'

import { IInputProps, Input } from 'core-components/library/inputs'
import { MultiSelectDynamic } from 'core-components/library/multiSelect'
import { Tooltip } from 'core-components/library/tooltip'
import { useStaticCrud } from 'core-components/types/helpers'
import {
  PartnerSearchFilterItem,
  PartnerSearchFilterProps,
} from 'partner360/components/partnerSearchFilter'
import { LIST_SEPARATOR } from 'partner360/components/partnerSearchFilter/configs'
import { compareByPartnerName } from 'partner360/components/partnerSearchFilter/helpers/compareByPartnerName'
import { parseClipboardTokens } from 'partner360/components/partnerSearchFilter/helpers/parseClipboardTokens'
import { resolvePartnersFromTokens } from 'partner360/components/partnerSearchFilter/helpers/resolvePartnersFromTokens'

import { PartnerSearchRenderItem } from '../partnerSearchRenderItem/PartnerSearchRenderItem'

import styles from './PartnerSearchFilter.module.scss'

const getUniquePartners = (partners: PartnerSearchFilterItem[]) => {
  const seenPartnerIds = new Set<string>()

  return partners.filter((partner) => {
    if (!partner.PartnerId || seenPartnerIds.has(partner.PartnerId)) {
      return false
    }

    seenPartnerIds.add(partner.PartnerId)
    return true
  })
}

export const PartnerSearchFilter: FC<PartnerSearchFilterProps> = ({
  label,
  value,
  partners,
  onChange,
  placeholder = 'Partner Name, Partner ID, Location ID',
  tooltipContent,
  className,
  partnerNameClassName,
  sortComparator = compareByPartnerName,
  disabled,
  withSearchIcon = false,
}) => {
  const [searchValue, setSearchValue] = useState('')

  const uniquePartners = useMemo(
    () => getUniquePartners(partners).sort(sortComparator),
    [partners, sortComparator]
  )

  const partnersSource = useStaticCrud<PartnerSearchFilterItem>({
    data: uniquePartners,
    keyField: 'PartnerId',
    displayField: 'PartnerName',
  })

  const ObservableInput = useMemo(() => {
    const ObservableInputComponent = forwardRef<HTMLInputElement, IInputProps>(
      ({ onChange: onInputChange, startContent, endContent, ...props }, ref) => {
        const handleChangeInput = (nextValue: string) => {
          setSearchValue(nextValue)
          onInputChange?.(nextValue)
        }

        const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
          const text = event.clipboardData.getData('text')
          if (!text.includes(LIST_SEPARATOR)) {
            return
          }

          event.preventDefault()

          const tokens = parseClipboardTokens(text)
          const resolvedPartnerIds = resolvePartnersFromTokens(tokens, uniquePartners)

          if (resolvedPartnerIds.length === 0) {
            return
          }

          onChange(Array.from(new Set([...value, ...resolvedPartnerIds])))
        }

        return (
          <div onPaste={handlePaste}>
            <Input
              {...props}
              ref={ref}
              placeholder={placeholder}
              onChange={handleChangeInput}
              startContent={
                withSearchIcon ? (
                  <div className={styles.startContent}>
                    <i className={cn('kbase kbase-search', styles.search)} aria-hidden="true" />
                    {startContent}
                  </div>
                ) : (
                  startContent
                )
              }
              endContent={
                <div className={styles.inputIcons}>
                  {tooltipContent && (
                    <Tooltip
                      tipContent={() => (
                        <div className={styles.tooltipMessage}>{tooltipContent}</div>
                      )}
                    >
                      <i className={cn('kbase kbase-info-alt', styles.info)} />
                    </Tooltip>
                  )}
                  {endContent}
                </div>
              }
            />
          </div>
        )
      }
    )

    ObservableInputComponent.displayName = 'PartnerSearchFilterInput'

    return ObservableInputComponent
  }, [onChange, placeholder, tooltipContent, uniquePartners, value, withSearchIcon])

  const itemRenderer = useCallback(
    (partner: PartnerSearchFilterItem) => {
      return (
        <PartnerSearchRenderItem
          partner={partner}
          searchValue={searchValue}
          partnerNameClassName={partnerNameClassName}
        />
      )
    },
    [partnerNameClassName, searchValue]
  )

  return (
    <MultiSelectDynamic
      classes={{
        controlClasses: {
          root: className,
        },
      }}
      closeOnSelect={false}
      source={partnersSource}
      caption="PartnerName"
      value={value}
      label={label as any}
      fieldsToSearch={['GeoMarket', 'Country', 'PartnerName', 'PartnerId', 'LocationId']}
      renderFunction={itemRenderer}
      onChange={onChange}
      InputComponent={ObservableInput}
      disabled={disabled}
    />
  )
}
